import { apiClient } from '@/core/api/client';
import { Endpoints } from '@/core/api/endpoints';
import type {
  BookingDetails,
  BookingResponse,
  CreateBookingRequest,
  ResendBookingFormRequest,
} from '@/core/api/models/bookings';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';

function resolveCustomerId(explicit?: string | null): string {
  const fromParam = explicit?.trim() ?? '';
  const customerId =
    fromParam ||
    useAppStore.getState().auth.user?.customer_id?.trim() ||
    '';
  if (!customerId) {
    throw new Error('Missing customer_id — load profile details before booking');
  }
  return customerId;
}

function requireValue(value: string | undefined, field: string): string {
  const normalized = value?.trim() ?? '';
  if (!normalized) {
    throw new Error(`${field} is required`);
  }
  return normalized;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function readDetails(value: unknown): BookingDetails | null {
  if (Array.isArray(value)) {
    return (asRecord(value[0]) as BookingDetails | null) ?? null;
  }
  return asRecord(value) as BookingDetails | null;
}

/** Read the first non-empty string/number field from a booking payload. */
export function readBookingText(
  booking: Record<string, unknown>,
  keys: readonly string[],
): string {
  for (const key of keys) {
    const value = booking[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }
  }
  return '';
}

function looksLikeBookingRecord(value: Record<string, unknown> | null): boolean {
  if (!value) {
    return false;
  }
  return Boolean(
    readBookingText(value, ['unit_id', 'unitId', 'property_id', 'parent_id', 'booking_id']),
  );
}

/** Normalize booking response wrappers to a single booking record. */
export function extractBookingDetails(response: BookingResponse | null | undefined): BookingDetails | null {
  if (!response) {
    return null;
  }
  const direct = readDetails(response.details) ?? readDetails(response.booking);
  if (looksLikeBookingRecord(direct as Record<string, unknown> | null)) {
    return direct;
  }
  const data = asRecord(response.data);
  const fromDataDetails = readDetails(data?.details);
  if (looksLikeBookingRecord(fromDataDetails as Record<string, unknown> | null)) {
    return fromDataDetails;
  }
  if (looksLikeBookingRecord(data)) {
    return data as BookingDetails;
  }
  const root = asRecord(response);
  if (looksLikeBookingRecord(root)) {
    return root as BookingDetails;
  }
  return null;
}

function readMessage(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * GET/POST bookings can answer HTTP 400 while the body reports success
 * (e.g. `{"error":"400","message":"Booking retrieved successfully."}`).
 * Treat that as success; genuine 400s still throw.
 */
function recoverDummySuccessResponse(
  error: unknown,
  operation: string,
): BookingResponse | null {
  const rejection = error as { status?: number; message?: string; response?: unknown } | null;
  if (rejection?.status !== 400) {
    return null;
  }
  const body = asRecord(rejection.response);
  const message = readMessage(body?.message) || readMessage(rejection.message);
  if (!/success/i.test(message)) {
    return null;
  }
  logger.warn(`BookingService:${operation} returned 400 with a success body`, {
    message,
  });
  if (body) {
    return { ...body, status: 'success', message };
  }
  return { status: 'success', message };
}

function assertBookingApiSuccess(response: BookingResponse, operation: string): void {
  const status = typeof response.status === 'string'
    ? response.status.trim().toLowerCase()
    : '';
  if (status !== 'error' && status !== 'failed' && status !== 'failure') {
    return;
  }
  throw new Error(
    typeof response.message === 'string' && response.message.trim()
      ? response.message
      : `${operation} failed`,
  );
}

/** ADHA web booking API. Bearer authentication is handled by apiClient. */
export class BookingService {
  async getBookingDetails(_leadId?: string | null): Promise<BookingResponse> {
    logger.info('BookingService:API getBookingDetails — calling');
    try {
      const response = await apiClient.get<BookingResponse>(Endpoints.booking.details);
      logger.info('BookingService:API getBookingDetails — response', { response });
      return response;
    } catch (error: unknown) {
      const recovered = recoverDummySuccessResponse(error, 'Get booking details');
      if (recovered) {
        logger.info('BookingService:API getBookingDetails — recovered response', {
          response: recovered,
        });
        return recovered;
      }
      logger.warn('BookingService:API getBookingDetails — failed', error);
      throw error;
    }
  }

  async createBooking(unitId: string, _leadId?: string | null): Promise<BookingResponse> {
    const payload: CreateBookingRequest = {
      unit_id: requireValue(unitId, 'unit_id'),
    };
    logger.info('BookingService:API createBooking — calling', payload);
    const response = await this.post(
      Endpoints.booking.create,
      payload,
      'Reserve unit',
    );
    logger.info('BookingService:API createBooking — response', { response });
    assertBookingApiSuccess(response, 'Reserve unit');
    return response;
  }

  /**
   * Cancel an active reservation — same POST /v1/bookings as create.
   * Empty `unit_id` cancels. Customer comes from the bearer token.
   */
  async cancelBooking(_leadId?: string | null): Promise<BookingResponse> {
    const payload: CreateBookingRequest = {
      unit_id: '',
    };
    logger.info('BookingService:API cancelBooking — calling', payload);
    const response = await this.post(
      Endpoints.booking.create,
      payload,
      'Cancel reservation',
    );
    logger.info('BookingService:API cancelBooking — response', { response });
    assertBookingApiSuccess(response, 'Cancel reservation');
    return response;
  }

  async resendBookingForm(
    parentId: string,
    customerId?: string | null,
  ): Promise<BookingResponse> {
    const payload: ResendBookingFormRequest = {
      doc_type: 'BOOKING_FORM',
      parent_id: requireValue(parentId, 'parent_id'),
      customer_id: resolveCustomerId(customerId),
    };
    logger.info('BookingService:API resendBookingForm — calling', payload);
    const response = await this.post(
      Endpoints.booking.resendForm,
      payload,
      'Resend booking form',
    );
    logger.info('BookingService:API resendBookingForm — response', { response });
    assertBookingApiSuccess(response, 'Resend booking form');
    return response;
  }

  private async post(
    url: string,
    payload: CreateBookingRequest | ResendBookingFormRequest,
    operation: string,
  ): Promise<BookingResponse> {
    try {
      return await apiClient.post<BookingResponse>(url, payload);
    } catch (error: unknown) {
      const recovered = recoverDummySuccessResponse(error, operation);
      if (recovered) {
        logger.info(`BookingService:API ${operation} — recovered response`, {
          response: recovered,
        });
        return recovered;
      }
      logger.warn(`BookingService:API ${operation} — failed`, error);
      throw error;
    }
  }
}

export const bookingService = new BookingService();
